// The Wi-Fi networks already in use at a location, WITH their passwords — so
// adding a second network to a device is a tap rather than a retyped password
// that has to be right first time.
//
// TWO SOURCES, because neither alone is complete:
//
//   1. The location's saved networks. See locationWifiRows for which of the
//      three server endpoints actually answer.
//   2. Asking the location's devices directly. `getallwifi` makes a device
//      report every network it holds, ssid and password, on its /client topic —
//      the same data the server stores, one hop earlier, and it reaches devices
//      whose credentials were pushed straight to them and never registered.
//
// Source 2 only reaches devices that are ONLINE. That is a real limit, not one
// to paper over: callers are told which source each network came from so the UI
// can say what it actually knows.

import { getActiveLocationWifi, getLocationWifiList, getDevicesByLocation } from '../api/api';
import { connectMqtt, subscribeTopic, addMessageListener, publishMessage } from '../mqtt/mqttClient';
import { CMD_GET_ALL_WIFI, parseWifiList } from '../devices/commands';

// How long to listen for `getallwifi` replies. A device answers in well under a
// second when it is online; the rest of the wait is only for stragglers, and a
// picker that makes you wait longer than this to see a list is not a shortcut.
export const REPLY_WINDOW_MS = 2500;

// Devices asked per location. Enough to cover a whole home, low enough that a
// large site does not fire a hundred publishes to fill one dialog.
export const MAX_DEVICES_ASKED = 25;

const unoOf = (d) => String(d?.UniqueNumber ?? '');

const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : []);

/**
 * The location's saved networks, best source first.
 *
 * ACTIVE first — it joins on devices seen in the last few minutes, so it drops
 * the router that was replaced last year along with the firmware's own
 * Smartify/Home fallbacks and blank passwords. It is not deployed yet (404), so
 * this falls through to the full list, and starts preferring the better answer
 * the day the route ships with no app change.
 *
 * ⚠️ AN EMPTY ACTIVE RESULT IS NOT AN ANSWER. It legitimately returns [] when
 * nothing has reported recently, so a site whose power was just cycled would
 * offer no networks at all. Empty falls through too.
 */
async function locationWifiRows(locationId) {
  const active = await getActiveLocationWifi(locationId).then(rowsOf).catch(() => null);
  if (active && active.length) return active;
  return getLocationWifiList(locationId).then(rowsOf).catch(() => null);
}

// Ask every device at this location what networks it holds, and collect the
// replies that arrive within the window.
async function askDevices(locationId) {
  const res = await getDevicesByLocation(locationId).catch(() => null);
  const unos = (Array.isArray(res?.data) ? res.data : [])
    .map(unoOf)
    .filter(Boolean)
    .slice(0, MAX_DEVICES_ASKED);
  if (!unos.length) return [];

  connectMqtt();
  const wanted = new Set(unos);
  const found = [];

  // Listen BEFORE publishing. A device can answer in milliseconds, and a
  // listener attached afterwards misses exactly the fastest replies.
  const detach = addMessageListener((topic, payload) => {
    const slash = String(topic).indexOf('/');
    const uno = slash === -1 ? String(topic) : String(topic).slice(0, slash);
    if (!wanted.has(uno) || !String(topic).endsWith('/client')) return;
    // ⚠️ THE WHOLE PAYLOAD, envelope and all. Stripping `^\[[^\]]*\]` here first
    // — which this did — also eats the opening pair of the ESP32's JSON reply,
    // `[["Home","Automation"]`. parseWifiList strips the envelope with a pattern
    // that cannot match JSON.
    parseWifiList(payload).forEach((w) => {
      if (w.ssid) found.push({ ssid: w.ssid, pass: w.pass || '', from: 'device', uno });
    });
  });

  unos.forEach((uno) => {
    subscribeTopic(`${uno}/client`);
    // Raw, no [uno]…#userId envelope — this is how the Flutter menu asks, and
    // the firmware answers either form.
    publishMessage(`${uno}/device`, CMD_GET_ALL_WIFI);
  });

  await new Promise((r) => { setTimeout(r, REPLY_WINDOW_MS); });
  detach();
  return found;
}

/**
 * Every network known for a location.
 *
 * Returns { networks, serverFailed, askedDevices } — `serverFailed` matters:
 * an empty list because the server hung is not the same claim as "this location
 * has no networks", and the UI must not conflate them.
 */
export async function getKnownNetworks(locationId) {
  if (locationId == null) return { networks: [], serverFailed: false, askedDevices: false };

  const [dbRows, fromDevices] = await Promise.all([
    locationWifiRows(locationId),
    askDevices(locationId),
  ]);

  // ⚠️ WIFISSID / WIFIPASSWORD, IN CAPS. The old code read WifiName /
  // WifiPassword, so even a good reply mapped to blanks and the list looked
  // empty for a second reason on top of the dead endpoint.
  const registered = (dbRows || [])
    .map((w) => ({
      ssid: w.WIFISSID ?? w.WifiName ?? '',
      pass: w.WIFIPASSWORD ?? w.WifiPassword ?? '',
      from: 'location',
    }))
    .filter((w) => w.ssid);

  // Registered first: where both know an SSID, the stored password is the one
  // someone deliberately recorded for the site.
  const bySsid = new Map();
  [...registered, ...fromDevices].forEach((w) => {
    if (!w.ssid) return;
    const prev = bySsid.get(w.ssid);
    // Prefer whichever entry actually carries a password.
    if (!prev || (!prev.pass && w.pass)) bySsid.set(w.ssid, w);
  });

  return {
    networks: [...bySsid.values()],
    serverFailed: dbRows === null,
    askedDevices: fromDevices.length > 0,
  };
}

/**
 * Merge what the phone can SEE with what we KNOW the password for.
 *
 * A network in both is ONE entry, not two: tap it and the password is already
 * filled in. A known network that is not in range is still listed — the phone
 * standing in the hall may not see a router the device on the far wall reaches
 * perfectly well.
 *
 * ⚠️ MATCHED ON THE EXACT SSID. They are case-sensitive, and "Home" is not
 * "home" — a loose match hands a device the wrong password, which fails as
 * "the device never joins" with nothing on screen to point at.
 *
 * -> [{ ssid, pass, inRange, known, band, level, from }]
 */
export function mergeScanWithKnown(scanned, known) {
  const bySsid = new Map();

  (known || []).forEach((k) => {
    if (!k.ssid) return;
    bySsid.set(k.ssid, {
      ssid: k.ssid, pass: k.pass || '', known: true, inRange: false, from: k.from,
    });
  });

  (scanned || []).forEach((n) => {
    if (!n.ssid) return;
    const prev = bySsid.get(n.ssid);
    bySsid.set(n.ssid, {
      ssid: n.ssid,
      pass: prev?.pass || '',
      known: Boolean(prev?.known),
      inRange: true,
      band: n.band,
      level: n.level,
      secured: n.secured,
      from: prev?.from,
    });
  });

  // In range first — those are the ones the user is most likely reaching for —
  // then known-but-absent, each group by name so the list does not reshuffle
  // between scans.
  return [...bySsid.values()].sort((a, b) => {
    if (a.inRange !== b.inRange) return a.inRange ? -1 : 1;
    return String(a.ssid).localeCompare(String(b.ssid));
  });
}
